import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Minus, Plus, Edit2, Check } from 'lucide-react';

interface QuantitySelectorProps {
  initialQuantity?: number;
  onQuantityChange: (qty: number) => void;
  label?: string;
  disabled?: boolean;
  min?: number;
  max?: number;
  size?: 'sm' | 'md' | 'lg';
  showPresets?: boolean;
}

export default function QuantitySelector({
  initialQuantity = 1,
  onQuantityChange,
  label,
  disabled = false,
  min = 0,
  max = 50,
  size = 'md',
  showPresets = false
}: QuantitySelectorProps) {
  const [quantity, setQuantity] = useState<number>(initialQuantity);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(initialQuantity.toString());
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setQuantity(initialQuantity);
    setInputValue(initialQuantity.toString());
  }, [initialQuantity]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const updateQuantity = (newQty: number) => {
    const clamped = Math.max(min, Math.min(max, newQty));
    setQuantity(clamped);
    setInputValue(clamped.toString());
    onQuantityChange(clamped);
  };

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || quantity <= min) return;
    updateQuantity(quantity - 1);
  };

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || quantity >= max) return;
    updateQuantity(quantity + 1);
  };

  const handleInputSubmit = () => {
    setIsEditing(false);
    let parsed = parseInt(inputValue, 10);
    if (isNaN(parsed) || parsed < min) {
      parsed = min;
    } else if (parsed > max) {
      parsed = max;
    }
    updateQuantity(parsed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleInputSubmit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setInputValue(quantity.toString());
    }
  };

  // Preset quantities for quick selection in larger dialogs
  const presets = [1, 2, 3, 5, 10];

  // Size styling maps
  const sizeStyles = {
    sm: {
      container: 'h-8 sm:h-9 px-1 bg-emerald-50/80 border border-emerald-200/80 rounded-xl shadow-xs',
      btn: 'w-6 h-6 sm:w-7 sm:h-7 rounded-lg text-emerald-800 hover:bg-emerald-600 hover:text-white active:scale-90 transition-all cursor-pointer flex items-center justify-center disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-emerald-800',
      icon: 'w-3.5 h-3.5 stroke-[2.5]',
      text: 'text-xs sm:text-sm font-black text-emerald-950 px-2 min-w-[28px] text-center',
      input: 'w-12 text-center text-xs font-black bg-white border border-emerald-400 rounded-lg py-0.5 px-1 text-emerald-950 focus:outline-none focus:ring-1 focus:ring-emerald-500'
    },
    md: {
      container: 'h-10 px-1.5 bg-gray-50 border border-brand-border rounded-xl shadow-xs',
      btn: 'w-7 h-7 rounded-lg text-brand-accent hover:bg-brand-accent hover:text-white active:scale-90 transition-all cursor-pointer flex items-center justify-center disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-brand-accent',
      icon: 'w-4 h-4 stroke-[2.5]',
      text: 'text-sm font-black text-brand-text px-3 min-w-[36px] text-center',
      input: 'w-14 text-center text-sm font-black bg-white border border-brand-accent rounded-lg py-1 px-1 text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-accent/20'
    },
    lg: {
      container: 'h-12 px-2 bg-emerald-50/60 border border-emerald-200 rounded-2xl shadow-sm',
      btn: 'w-9 h-9 rounded-xl bg-white text-emerald-800 shadow-sm border border-emerald-200/60 hover:bg-emerald-700 hover:text-white hover:border-emerald-700 active:scale-90 transition-all cursor-pointer flex items-center justify-center disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-emerald-800',
      icon: 'w-4 h-4 stroke-[3]',
      text: 'text-base font-black text-emerald-950 px-4 min-w-[44px] text-center',
      input: 'w-16 text-center text-base font-black bg-white border-2 border-emerald-600 rounded-xl py-1 px-1 text-emerald-950 focus:outline-none focus:ring-2 focus:ring-emerald-500/20'
    }
  };

  const currentStyle = sizeStyles[size] || sizeStyles.md;

  return (
    <div className="flex flex-col gap-1 w-full">
      {label && (
        <div className="flex items-center justify-between mb-0.5">
          <label className="text-[11px] font-black text-brand-muted uppercase tracking-wider">
            {label}
          </label>
          <span className="text-[10px] text-emerald-600 font-extrabold bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200/60">
            {quantity} {quantity === 1 ? 'unit' : 'units'}
          </span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap sm:flex-nowrap w-full">
        {/* Main Stepper Control */}
        <div className={`flex items-center justify-between ${currentStyle.container} transition-all shrink-0`}>
          {/* Decrement Button */}
          <motion.button
            type="button"
            whileTap={{ scale: 0.85 }}
            onClick={handleDecrement}
            disabled={disabled || quantity <= min}
            className={currentStyle.btn}
            title="Reduce quantity / मात्रा घटाएं"
            aria-label="Decrease quantity"
          >
            <Minus className={currentStyle.icon} />
          </motion.button>

          {/* Value Display or Inline Input */}
          {isEditing ? (
            <div className="flex items-center gap-1">
              <input
                ref={inputRef}
                type="number"
                min={min}
                max={max}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onBlur={handleInputSubmit}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                className={currentStyle.input}
              />
              <button
                type="button"
                onClick={handleInputSubmit}
                className="p-1 text-emerald-600 hover:text-emerald-800 cursor-pointer"
                title="Save quantity"
              >
                <Check className="w-3.5 h-3.5 stroke-[3]" />
              </button>
            </div>
          ) : (
            <div
              onClick={() => !disabled && setIsEditing(true)}
              className={`${currentStyle.text} cursor-pointer hover:scale-105 transition-transform flex items-center justify-center gap-1 group select-none`}
              title="Click to enter custom quantity / मात्रा बदलें"
            >
              <AnimatePresence mode="wait">
                <motion.span
                  key={quantity}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  transition={{ duration: 0.12 }}
                >
                  {quantity}
                </motion.span>
              </AnimatePresence>
              <Edit2 className="w-2.5 h-2.5 text-emerald-600/40 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          )}

          {/* Increment Button */}
          <motion.button
            type="button"
            whileTap={{ scale: 0.85 }}
            onClick={handleIncrement}
            disabled={disabled || quantity >= max}
            className={currentStyle.btn}
            title="Increase quantity / मात्रा बढ़ाएं"
            aria-label="Increase quantity"
          >
            <Plus className={currentStyle.icon} />
          </motion.button>
        </div>

        {/* Quick Presets Row (Optional) */}
        {showPresets && (
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar justify-end flex-grow">
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider shrink-0 mr-1">
              Quick:
            </span>
            {presets.map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => updateQuantity(num)}
                disabled={disabled}
                className={`px-2 py-0.5 sm:px-2.5 sm:py-1 text-[10px] font-black rounded-lg transition-all cursor-pointer shrink-0 border ${
                  quantity === num
                    ? 'bg-brand-accent text-white border-brand-accent shadow-2xs'
                    : 'bg-gray-50 text-brand-text border-brand-border hover:bg-gray-100 hover:border-gray-300'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

